import React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import Avatar from 'components/Avatar'
import AvatarProgress from 'components/AvatarProgress'
import Pill from 'components/Pill'
import {
  DateCell,
  ImageCell,
  TableCell,
  TableRow,
  TextCell,
} from 'components/Table/ClickableTable'
import Tooltip from 'components/Tooltip'
import link from 'infra/link'
import {
  ColumnDensity,
  formattedJobScopeChangeRequestStatus,
  formattedProgressionStep,
  formattedWaitingOn,
} from 'jobs/infra/JobsTableColumns'
import SearchableJob, { SearchableMilestone } from 'jobs/SearchableJob'
import JobManagementViewModel from '../../JobManagementViewModel'
import { Id } from 'types/definitions'
import { timeago, formatToLongDate } from 'utils/date-util'
import { CellProps, DataTestProps } from 'components/Table/TableCell'
import Icon from 'components/Icon'
import UnreadBadge from 'components/UnreadBadge'
import { Resolution } from 'jobs/Job'
import { JobStatus } from 'jobs/dtos/job'
import { JobTicket } from 'jobs/tickets/JobTicket'
import styles from './JobsTableRow.sass'
import { expr } from 'mobx-utils'
import Checkbox from '../../../../../components/Checkbox'

interface JobsTableRowProps {
  vm: JobManagementViewModel
  job: SearchableJob
}

export default observer(({ vm, job }: JobsTableRowProps) => (
  <TableRow
    to={!job.archived ? link.job(Id.assert(job.id)) : undefined}
    data-test={'job-list-item-' + job.name}
  >
    <JobsTableCell vm={vm} job={job} />
  </TableRow>
))

interface JobsTableCell {
  vm: JobManagementViewModel
  job: SearchableJob
}

export const JobsTableCell = observer(({ vm, job }: JobsTableCell) => {
  const columns = vm.columnManager.filteredColumns
  const density = vm.columnManager.density
  const selectionVM = vm.selectionVM

  return columns
    .filter((col) => col.visible)
    .map((col) => {
      switch (col.id) {
        case 'checkbox':
          return (
            <TableCell key={col.id}>
              <div>
                <Checkbox
                  muteClickEvent
                  checked={selectionVM.isSelectedForActions(job)}
                  onChange={() => selectionVM.toggleJobSelectionForActions(job)}
                />
              </div>
            </TableCell>
          )
        case 'jobTitle':
          return (
            <PrimaryCell
              job={job}
              openTicket={vm.getLatestOpenTicketForJob(job)}
              density={density}
              dataTest={`job-title-${job.id}`}
              key={col.id}
            />
          )
        case 'jobShortId':
          return (
            <TextCell dataTest={`job-short-id-${job.id}`} key={col.id}>
              {job.shortId}
            </TextCell>
          )
        case 'legendName':
          return (
            <TextCell dataTest={`job-legend-name-${job.id}`} key={col.id}>
              {job.legendName}
            </TextCell>
          )
        case 'workspace':
          return (
            <ImageCell
              title={job.workspace?.name}
              image={job.workspace?.branding?.logos?.icon}
              dataTest={`job-workspace-${job.id}`}
              key={col.id}
            />
          )
        case 'status':
          return (
            <StatusCell
              resolution={job.resolution}
              status={job.status}
              dataTest={`job-status-${job.id}`}
              key={col.id}
            />
          )
        case 'milestone':
          return (
            <MilestoneCell
              job={job}
              density={density}
              dataTest={`job-milestone-${job.id}`}
              key={col.id}
            />
          )
        case 'waitingOn':
          return (
            <PillCell dataTest={`job-waiting-on-${job.id}`} key={col.id}>
              {formattedWaitingOn(job.waitingOn)}
            </PillCell>
          )
        case 'payout':
          return (
            <TextCell dataTest={`job-payout-${job.id}`} key={col.id}>
              {job.formattedProviderCutAmount}
            </TextCell>
          )
        case 'total':
          return (
            <TotalCell
              job={job}
              density={density}
              dataTest={`job-total-${job.id}`}
              key={col.id}
            />
          )
        case 'progressionStep':
          return (
            <TextCell dataTest={`job-progression-step-${job.id}`} key={col.id}>
              {formattedProgressionStep(job)}
            </TextCell>
          )
        case 'jobScopeChangeRequestStatus':
          return (
            <PillCell
              dataTest={`job-scope-change-request-status-${job.id}`}
              key={col.id}
            >
              {formattedJobScopeChangeRequestStatus(job.scopeChangeStatus)}
            </PillCell>
          )
        case 'clientTeam':
          return (
            <TextCell dataTest={`job-client-team-${job.id}`} key={col.id}>
              {job.clientTeamName}
            </TextCell>
          )
        case 'proTeam':
          return (
            <TextCell dataTest={`job-pro-team-${job.id}`} key={col.id}>
              {job.proTeamName}
            </TextCell>
          )
        case 'clientChampion':
          return (
            <ImageCell
              title={job.championClient?.displayName}
              image={job.championClient?.avatar}
              imageFallback={job.championClient?.avatarFallback}
              dataTest={`job-client-champion-${job.id}`}
              key={col.id}
            />
          )
        case 'proChampion':
          return (
            <ImageCell
              title={job.championPro?.displayName}
              image={job.championPro?.avatar}
              imageFallback={job.championPro?.avatarFallback}
              dataTest={`job-pro-champion-${job.id}`}
              key={col.id}
            />
          )
        case 'isRequestedPro':
          return (
            <TextCell dataTest={`job-is-requested-pro-${job.id}`} key={col.id}>
              {job.isRequestedPro ? 'Yes' : 'No'}
            </TextCell>
          )
        case 'created':
          return (
            <DateCell
              date={job.dateCreated}
              density={density}
              dataTest={`job-created-date-${job.id}`}
              key={col.id}
            />
          )
        case 'submitted':
          return (
            <DateCell
              date={job.dateSubmitted}
              density={density}
              dataTest={`job-submitted-date-${job.id}`}
              key={col.id}
            />
          )
        case 'claimed':
          return (
            <DateCell
              date={job.dateAccepted}
              density={density}
              dataTest={`job-claimed-date-${job.id}`}
              key={col.id}
            />
          )
        case 'deadline':
          return (
            <DateCell
              date={job.dateDeadline}
              density={density}
              dataTest={`job-deadline-date-${job.id}`}
              key={col.id}
            />
          )
        case 'completed':
          return job.status === 'CLOSED' ? (
            <DateCell
              date={job.dateClosed}
              density={density}
              dataTest={`job-completed-date-${job.id}`}
              key={col.id}
            />
          ) : (
            <TableCell key={col.id} />
          )
        case 'lastActivity':
          return (
            <DateCell
              date={job.dateLastActivity}
              density={density}
              dataTest={`job-last-activity-date-${job.id}`}
              key={col.id}
            />
          )
        default:
          return <TableCell key={col.id} />
      }
    })
})

function timeSpentInMilestone(
  currentMilestone: SearchableMilestone | null,
  job: SearchableJob,
): string | null {
  // if there is no milestone return an empty string
  if (!currentMilestone || !job.isInProgress) {
    return null
  }
  const allMilestones = job.milestones
  // if the current milestone is the first one then use the submitted date
  let indexOfCurrentMilestone = allMilestones.indexOf(currentMilestone)
  if (indexOfCurrentMilestone === 0) {
    return job.dateSubmitted ? timeago(job.dateSubmitted!, 'long') : null
  }
  // we know for sure that indexOfCurrentMilestone is at least 1
  indexOfCurrentMilestone = indexOfCurrentMilestone - 1
  // since its not the first one we have to get the one before and check the last task completion date
  let previousMilestone = job.milestones[indexOfCurrentMilestone]

  // we should not have milestones without tasks but JH of course
  while (
    previousMilestone.tasksList.length === 0 &&
    indexOfCurrentMilestone > 0
  ) {
    indexOfCurrentMilestone = indexOfCurrentMilestone - 1
    previousMilestone = job.milestones[indexOfCurrentMilestone]
  }

  // in case we dont have a previous milestone (which should never happen) let's just use submitted date
  // in case the previousMilestone doesn't have any tasks use submitted date
  // the code above should be looping through previous tasks so worst case we get to the first one and then we show time since submitted date
  if (!previousMilestone || previousMilestone.tasksList.length === 0) {
    return job.dateSubmitted ? timeago(job.dateSubmitted!, 'long') : null
  }
  return timeago(
    previousMilestone.tasksList[previousMilestone.tasksList.length - 1]
      .dateCompleted,
    'long',
  )
}

interface JobCellProps {
  job: SearchableJob
  density: ColumnDensity
}

interface PrimaryCellProps extends JobCellProps, DataTestProps {
  openTicket: JobTicket | null
}

export const PrimaryCell = observer(function PrimaryCell({
  job,
  openTicket,
  density,
  dataTest,
}: PrimaryCellProps) {
  const subjects =
    job.currentUserMember?.type === 'CLIENT' && job.proMembers.length
      ? job.proMembers
      : job.clientMembers
  const showAvatar = density === ColumnDensity.Default
  const showSecondaryInfo = density !== ColumnDensity.Compact
  const unreadCount = job.conversation?.unreadCount ?? 0

  return (
    <TableCell>
      <div className={styles.primaryCellContainer}>
        <div className={styles.avatarContainer}>
          {unreadCount > 0 && (
            <UnreadBadge count={unreadCount} className={styles.unreadBadge} />
          )}
          {showAvatar && (
            <AvatarProgress
              size={36}
              progress={job.milestoneCompletionPercentage}
            >
              <Avatar size={24} subject={subjects} />
            </AvatarProgress>
          )}
        </div>
        <div className={styles.primaryInfo}>
          <span className={styles.primaryText} data-test={dataTest}>
            {job.displayName}
            {openTicket && (
              <>
                <span className={styles.primaryTextSeparator}>&bull;</span>
                <OpenTicket openTicket={openTicket} />
              </>
            )}
          </span>
          {showSecondaryInfo && (
            <span className={styles.secondaryInfo}>{job.secondaryInfo}</span>
          )}
        </div>
      </div>
    </TableCell>
  )
})

const OpenTicket = observer(function OpenTicket({
  openTicket,
}: {
  openTicket: JobTicket
}) {
  return (
    <Tooltip
      placement="top-start"
      content={
        <>
          {openTicket.statusMessage && <p>{openTicket.statusMessage}</p>}
          <p>Last updated {formatToLongDate(openTicket.lastUpdated)}</p>
        </>
      }
    >
      <span className={styles.openTicket}>
        Open ticket
        <span className={styles.openTicketIcon}>
          <Icon auto name="alert-blue" />
        </span>
      </span>
    </Tooltip>
  )
})

export const MilestoneCell = observer(function MilestoneCell({
  job,
  density,
  dataTest,
}: JobCellProps & DataTestProps) {
  const completedCount =
    job.currentMilestone &&
    job.currentMilestone.tasksList.filter((t) => t.completed).length
  const totalCount =
    job.currentMilestone && job.currentMilestone.tasksList.length
  const timeSinceLastMilestoneCompleted = timeSpentInMilestone(
    job.currentMilestone,
    job,
  )

  return (
    <TableCell>
      <div className={styles.primaryCellContainer}>
        {job.currentMilestone && !job.isProgressionFlowEnabled && (
          <>
            <div className={styles.milestoneCellInnerContainer}>
              <div>
                <span className={styles.milestoneName} data-test={dataTest}>
                  {job.currentMilestone && job.currentMilestone.title}
                </span>
                <span
                  className={styles.milestoneOfTotal}
                >{`(${completedCount}/${totalCount})`}</span>
              </div>
              {timeSinceLastMilestoneCompleted && (
                <Tooltip content={`Time spent in Milestone`}>
                  <>
                    {density === ColumnDensity.Default && (
                      <span className={styles.timeSinceLastMilestoneCompleted}>
                        {timeSinceLastMilestoneCompleted}
                      </span>
                    )}
                  </>
                </Tooltip>
              )}
            </div>
          </>
        )}
      </div>
    </TableCell>
  )
})

export const PillCell = observer(function PillCell({
  children,
  dataTest,
}: CellProps & DataTestProps) {
  return (
    <TableCell>
      {children && <Pill data-test={dataTest}>{children}</Pill>}
    </TableCell>
  )
})

interface StatusCellProps {
  resolution: Resolution | null | undefined
  status: JobStatus
}

export const StatusCell = observer(function StatusCell({
  resolution,
  status,
  dataTest,
}: StatusCellProps & DataTestProps) {
  const formattedResolution = (res: typeof resolution) => {
    switch (res) {
      case Resolution.Cancelled:
        return 'cancelled'
      case Resolution.Test:
        return 'test'
      default:
        return undefined
    }
  }

  const formattedStatus = expr(() => {
    switch (status) {
      case 'UNDER_CONSTRUCTION':
        return { text: 'Onboarding', className: styles.statusOnboarding }
      case 'INFO_GATHERING':
        return { text: 'Info Gathering', className: styles.statusInfoGathering }
      case 'UNCLAIMED':
        return { text: 'Unclaimed', className: styles.statusUnclaimed }
      case 'CLAIMED':
        return { text: 'In Progress', className: styles.statusClaimed }
      case 'CLOSED':
        return {
          text: 'Closed',
          subtext: formattedResolution(resolution),
          className: styles.statusClosed,
        }
      case 'IDLE':
        return { text: 'Idle', className: styles.statusIdle }
      case 'ON_HOLD':
        return { text: 'On Hold', className: styles.statusOnHold }
      default:
        return { text: 'Unspecified' }
    }
  })

  return (
    <TableCell className={cx(styles.statusCell, formattedStatus.className)}>
      <span data-test={dataTest}>
        {formattedStatus.text}
        {formattedStatus.subtext && (
          <span className={styles.statusSubtext}>
            {` (${formattedStatus.subtext})`}
          </span>
        )}
      </span>
    </TableCell>
  )
})

export const TotalCell = observer(function TotalCell({
  job,
  dataTest,
}: JobCellProps & DataTestProps) {
  return (
    <TextCell dataTest={dataTest}>
      {job.shouldShowJobTotal ? job.formattedTotalPrice : ''}
    </TextCell>
  )
})
